import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { productAPI, categoryAPI, sliderAPI, testimonialAPI, IMAGE_BASE } from '../../services/api';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useCart } from '../../context/CartContext';
import useScrollReveal from '../../hooks/useScrollReveal';
import {
    ChevronLeft, ChevronRight, ArrowRight,
    Star, Eye, Truck, ShoppingCart, Frame, Tag, Clock, Heart
} from 'lucide-react';
import { useFavorites } from '../../context/FavoritesContext';
import useSeo from '../../hooks/useSeo';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);


function TestimonialsSlider({ testimonials }) {
    const trackRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [visibleCount, setVisibleCount] = useState(3);
    const [isMobile, setIsMobile] = useState(false);
    const autoPlayRef = useRef(null);

    useEffect(() => {
        const updateVisible = () => {
            const w = window.innerWidth;
            setIsMobile(w <= 768);
            if (w <= 1024) setVisibleCount(2);
            else setVisibleCount(3);
        };
        updateVisible();
        window.addEventListener('resize', updateVisible);
        return () => window.removeEventListener('resize', updateVisible);
    }, []);

    const maxIndex = Math.max(0, testimonials.length - visibleCount);

    const goTo = useCallback((idx) => {
        setCurrentIndex(Math.max(0, Math.min(idx, maxIndex)));
    }, [maxIndex]);

    useEffect(() => {
        if (isMobile || testimonials.length === 0) return;
        autoPlayRef.current = setInterval(() => {
            setCurrentIndex(prev => prev >= maxIndex ? 0 : prev + 1);
        }, 4000);
        return () => clearInterval(autoPlayRef.current);
    }, [maxIndex, isMobile, testimonials.length]);

    const pauseAuto = () => clearInterval(autoPlayRef.current);
    const resumeAuto = () => {
        if (isMobile) return;
        clearInterval(autoPlayRef.current);
        autoPlayRef.current = setInterval(() => {
            setCurrentIndex(prev => prev >= maxIndex ? 0 : prev + 1);
        }, 4000);
    };

    if (testimonials.length === 0) return null;

    if (isMobile) {
        return (
            <section className="testimonials-section">
                <div className="np-section-head reveal">
                    <span className="np-section-overtitle"><Star size={12} /> Müşteri Yorumları</span>
                    <h2>Müşterilerimizden</h2>
                </div>
                <div className="testimonials-grid-mobile reveal">
                    {testimonials.map((t, i) => (
                        <div key={t.id || i} className="testimonial-card-mobile">
                            <div className="testimonial-mobile-left">
                                <div className="testimonial-avatar">{t.initials}</div>
                            </div>
                            <div className="testimonial-mobile-right">
                                <div className="testimonial-mobile-top">
                                    <span className="testimonial-mobile-name">{t.name}</span>
                                    <div className="testimonial-stars">
                                        {[...Array(5)].map((_, si) => (
                                            <Star key={si} size={10} fill={si < t.rating ? '#f9ca24' : 'none'} />
                                        ))}
                                    </div>
                                </div>
                                <p className="testimonial-mobile-text">{t.text}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    return (
        <section className="testimonials-section" onMouseEnter={pauseAuto} onMouseLeave={resumeAuto}>
            <div className="np-section-head reveal">
                <span className="np-section-overtitle"><Star size={12} /> Müşteri Yorumları</span>
                <h2>Müşterilerimizden</h2>
                <p>Tablolarımızı tercih edenlerin yorumları</p>
            </div>

            <div className="testimonials-slider reveal">
                <button className="testimonial-arrow testimonial-arrow-left" onClick={() => goTo(currentIndex - 1)} disabled={currentIndex === 0}>
                    <ChevronLeft size={22} />
                </button>

                <div className="testimonials-track-wrapper">
                    <div
                        ref={trackRef}
                        className="testimonials-track"
                        style={{ transform: `translateX(-${currentIndex * (100 / visibleCount)}%)` }}
                    >
                        {testimonials.map((t, i) => (
                            <div key={t.id || i} className="testimonial-slide" style={{ minWidth: `${100 / visibleCount}%` }}>
                                <div className="testimonial-card">
                                    <div className="testimonial-header">
                                        <div className="testimonial-avatar">{t.initials}</div>
                                        <div className="testimonial-info">
                                            <h4>{t.name}</h4>
                                            <span className="testimonial-date">{t.date}</span>
                                        </div>
                                    </div>
                                    <div className="testimonial-stars">
                                        {[...Array(5)].map((_, si) => (
                                            <Star key={si} size={16} fill={si < t.rating ? '#f9ca24' : 'none'} />
                                        ))}
                                    </div>
                                    <p className="testimonial-text">{t.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <button className="testimonial-arrow testimonial-arrow-right" onClick={() => goTo(currentIndex + 1)} disabled={currentIndex >= maxIndex}>
                    <ChevronRight size={22} />
                </button>
            </div>

            <div className="testimonial-dots">
                {Array.from({ length: maxIndex + 1 }, (_, i) => (
                    <button key={i} className={`testimonial-dot ${i === currentIndex ? 'active' : ''}`} onClick={() => goTo(i)} />
                ))}
            </div>
        </section>
    );
}

// Product card component - imported from shared component
import ProductCard from '../../components/ProductCard';

export default function Home() {
    useSeo({
        title: 'Atölye Kirpi | Kanvas Tablo, Duvar Tablosu & Dekoratif Tablolar',
        description: 'Kanvas tablo, çerçeveli tablo, modern duvar tabloları ve dekoratif panolar. Eviniz için özenle seçilmiş tablo koleksiyonları. Ücretsiz kargo.',
        keywords: 'kanvas tablo, duvar tablosu, dekoratif tablo, çerçeveli tablo, modern tablo, tablo satın al, online tablo mağazası',
        canonical: 'https://xn--atlyekirpi-fcb.com.tr/'
    });
    const [featured, setFeatured] = useState([]);
    const [newArrivals, setNewArrivals] = useState([]);
    const [discounted, setDiscounted] = useState([]);
    const [categories, setCategories] = useState([]);
    const [heroSlides, setHeroSlides] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addingId, setAddingId] = useState(null);
    const navigate = useNavigate();
    const { addItem } = useCart();
    const { isFavorited, toggleFavorite } = useFavorites();
    const revealRef = useScrollReveal();
    const { settings } = useSiteSettings();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [prodRes, newRes, dealsRes, catRes, sliderRes, testimonialRes] = await Promise.all([
                    productAPI.getAll({ featured: 'true', active: 'true', limit: 8 }),
                    productAPI.getAll({ active: 'true', sort: 'newest', limit: 6 }),
                    productAPI.getAll({ deals: 'true', active: 'true', limit: 4 }),
                    categoryAPI.getAll({ active: 'true' }),
                    sliderAPI.getAll({ active: 'true' }),
                    testimonialAPI.getAll({ active: 'true' })
                ]);
                const allFeatured = prodRes.data.products || [];
                const allNew = newRes.data.products || [];
                const dealProducts = dealsRes.data.products || [];
                setFeatured(allFeatured);
                setNewArrivals(allNew.slice(0, 6));
                setDiscounted(dealProducts);
                setCategories((catRes.data || []).filter(c => !c.parentId));
                setHeroSlides(sliderRes.data || []);
                setTestimonials(testimonialRes.data || []);
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        fetchData();
    }, []);

    // GSAP — Öne Çıkan Tablolar animasyonu
    const featuredRef = useRef(null);
    useEffect(() => {
        if (!featuredRef.current || featured.length === 0) return;
        const section = featuredRef.current;
        const overtitle = section.querySelector('.featured-overtitle');
        const fireIcon = section.querySelector('.fire-icon');
        const title = section.querySelector('.featured-title');
        const subtitle = section.querySelector('.featured-subtitle');
        const cards = section.querySelectorAll('.product-card');
        const cta = section.querySelector('.featured-cta');

        gsap.set([overtitle, title, subtitle, cta].filter(Boolean), { opacity: 0, y: 30 });
        gsap.set(cards, { opacity: 0, y: 40, rotateY: -8 });

        const st = ScrollTrigger.create({
            trigger: section,
            start: 'top 78%',
            once: true,
            onEnter: () => {
                const tl = gsap.timeline();

                // Overtitle giriş
                if (overtitle) {
                    tl.to(overtitle, {
                        opacity: 1, y: 0,
                        duration: 0.5, ease: 'power3.out'
                    }, 0);
                }

                // Ateş ikonu — sürekli titreşim + scale
                if (fireIcon) {
                    tl.fromTo(fireIcon,
                        { scale: 0, rotation: -20 },
                        { scale: 1.2, rotation: 0, duration: 0.5, ease: 'back.out(3)' },
                        0.1
                    );
                    tl.to(fireIcon, { scale: 1, duration: 0.2 }, 0.6);
                }

                // Title — split text
                if (title) {
                    const text = title.textContent;
                    const words = text.split(' ');
                    title.innerHTML = words.map(w =>
                        `<span class="word-wrap"><span class="word">${w}</span></span>`
                    ).join(' ');
                    const wordEls = title.querySelectorAll('.word');
                    gsap.set(title, { opacity: 1, y: 0 });
                    gsap.set(wordEls, { y: '120%', rotateX: -40 });
                    tl.to(wordEls, {
                        y: '0%', rotateX: 0,
                        duration: 0.65, ease: 'power3.out',
                        stagger: 0.06
                    }, 0.2);
                }

                // Subtitle
                if (subtitle) {
                    tl.to(subtitle, {
                        opacity: 1, y: 0,
                        duration: 0.5, ease: 'power2.out'
                    }, 0.5);
                }

                // Kartlar — fan-out stagger
                tl.to(cards, {
                    opacity: 1, y: 0, rotateY: 0,
                    duration: 0.55, ease: 'power3.out',
                    stagger: { each: 0.08, from: 'center' }
                }, 0.6);

                // CTA buton
                if (cta) {
                    tl.to(cta, {
                        opacity: 1, y: 0,
                        duration: 0.5, ease: 'power2.out'
                    }, 1.1);
                }
            }
        });

        return () => st.kill();
    }, [featured]);

    // GSAP — İndirimler bölümü animasyonu
    const dealsRef = useRef(null);
    useEffect(() => {
        if (!dealsRef.current || discounted.length === 0) return;
        const section = dealsRef.current;
        const overtitle = section.querySelector('.deals-overtitle');
        const title = section.querySelector('.deals-title');
        const subtitle = section.querySelector('.deals-subtitle');
        const cards = section.querySelectorAll('.product-card');

        gsap.set([overtitle, title, subtitle].filter(Boolean), { opacity: 0, y: 30 });
        gsap.set(cards, { opacity: 0, y: 50, scale: 0.92 });

        const st = ScrollTrigger.create({
            trigger: section,
            start: 'top 75%',
            once: true,
            onEnter: () => {
                const tl = gsap.timeline();

                // Overtitle — hızlıca kayarak giriş
                if (overtitle) {
                    tl.to(overtitle, {
                        opacity: 1, y: 0,
                        duration: 0.5, ease: 'power3.out'
                    }, 0);
                }

                // Title — kelime kelime reveal
                if (title) {
                    const text = title.textContent;
                    const words = text.split(' ');
                    title.innerHTML = words.map(w =>
                        `<span class="word-wrap"><span class="word">${w}</span></span>`
                    ).join(' ');
                    const wordEls = title.querySelectorAll('.word');
                    gsap.set(title, { opacity: 1, y: 0 });
                    gsap.set(wordEls, { y: '120%' });
                    tl.to(wordEls, {
                        y: '0%', duration: 0.6, ease: 'power3.out',
                        stagger: 0.07
                    }, 0.15);
                }

                // Subtitle
                if (subtitle) {
                    tl.to(subtitle, {
                        opacity: 1, y: 0,
                        duration: 0.5, ease: 'power2.out'
                    }, 0.4);
                }

                // Kartlar — sırayla flip-up giriş
                tl.to(cards, {
                    opacity: 1, y: 0, scale: 1,
                    duration: 0.6, ease: 'back.out(1.4)',
                    stagger: 0.1
                }, 0.5);

                // İlk kartın discount badge'ine pulse efekti
                const firstBadge = cards[0]?.querySelector('.badge-discount');
                if (firstBadge) {
                    tl.fromTo(firstBadge,
                        { scale: 1 },
                        { scale: 1.2, duration: 0.3, ease: 'power2.out', yoyo: true, repeat: 2 },
                        1.1
                    );
                }
            }
        });

        return () => st.kill();
    }, [discounted]);

    const formatPrice = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);

    const handleAddToCart = async (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        if (product.hasVariants) {
            navigate(`/urun/${product.slug}`);
            return;
        }
        setAddingId(product.id);
        await addItem(product.id);
        setAddingId(null);
    };

    return (
        <div ref={revealRef}>
            {/* Hero Section */}
            {heroSlides.length > 0 && (
                <section className="hero-cards-section">
                    {/* Background image from first slide */}
                    {(() => {
                        const bgUrl = heroSlides[0]?.image;
                        return bgUrl ? <div className="hero-bg-image" style={{ backgroundImage: `url(${bgUrl.startsWith('http') ? bgUrl : IMAGE_BASE + bgUrl})` }} /> : null;
                    })()}
                    <div className="hero-top-content">
                        <span className="hero-cards-overtitle"><Frame size={14} /> Tablo Koleksiyonu</span>
                        <h1 className="hero-cards-title">Duvarlarınıza <span>Karakter</span> Katın</h1>
                        <p className="hero-cards-desc">Kanvas tablolar, modern baskılar ve dekoratif panolarla yaşam alanlarınızı dönüştürün.</p>
                        <Link to="/urunler" className="hero-explore-btn">
                            <span>Koleksiyonu Keşfet</span>
                            <ArrowRight size={15} />
                        </Link>
                    </div>
                    <div className="hero-scroll-track">
                        <div className="hero-scroll-inner">
                            {[...heroSlides, ...heroSlides].map((slide, index) => {
                                const imgUrl = slide.image ? (slide.image.startsWith('http') ? slide.image : IMAGE_BASE + slide.image) : null;
                                return (
                                    <Link to={slide.buttonLink || '/urunler'} className="hero-scroll-card" key={index}>
                                        {imgUrl && <img src={imgUrl} alt={slide.title} loading={index < 3 ? 'eager' : 'lazy'} />}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Marquee */}
            {(() => {
                let items = ['Kanvas Tablo', 'Çerçeveli Tablo', 'Modern Duvar Sanatı', 'Ücretsiz Kargo', 'Dekoratif Panolar'];
                try { const parsed = JSON.parse(settings.marqueeItems); if (Array.isArray(parsed) && parsed.length) items = parsed; } catch {}
                return items.length > 0 && (
                    <div className="marquee-strip">
                        <div className="marquee-track">
                            {[...Array(2)].map((_, i) => (
                                <div className="marquee-content" key={i} aria-hidden={i > 0}>
                                    {[...items, ...items].map((text, j) => (
                                        <span key={j}>{text}<span className="marquee-dot" /></span>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })()}

            {/* Slim Promo Banner — kaldırıldı */}

            {/* Category Cards — kaldırıldı */}

            {/* Öne Çıkan Ürünler (Featured) */}
            <section className="np-home-section featured-section" ref={featuredRef}>
                <div className="np-section-head">
                    <span className="np-section-overtitle featured-overtitle">
                        <span className="fire-icon">—</span> Editörün Seçimi
                    </span>
                    <h2 className="featured-title">Öne Çıkan Tablolar</h2>
                    <p className="featured-subtitle">En çok tercih edilen koleksiyon parçaları</p>
                </div>

                {loading ? (
                    <div className="loading"><div className="spinner"></div></div>
                ) : (
                    <>
                        <div className="products-grid featured-grid">
                            {featured.map(product => (
                                <ProductCard
                                    key={product.id}
                                    product={product}
                                    formatPrice={formatPrice}
                                    addingId={addingId}
                                    handleAddToCart={handleAddToCart}
                                    isFavorited={isFavorited}
                                    toggleFavorite={toggleFavorite}
                                    showDescription
                                    showShippingBadges
                                    discountLabel
                                />
                            ))}
                        </div>
                        <div className="np-section-cta featured-cta">
                            <Link to="/urunler" className="np-view-all-btn">
                                <span>Tüm Ürünleri Gör</span>
                                <ArrowRight size={15} />
                            </Link>
                        </div>
                    </>
                )}
            </section>


            {/* Haftanın İndirimleri */}
            {discounted.length > 0 && (
                <section className="np-home-section np-home-section-alt deals-section" ref={dealsRef}>
                    <div className="np-section-head">
                        <span className="np-section-overtitle deals-overtitle"><Tag size={12} /> Fırsat Tabloları</span>
                        <h2 className="deals-title">İndirimli Koleksiyon</h2>
                        <p className="deals-subtitle">Sınırlı süre, özel fiyatlar</p>
                    </div>
                    <div className="products-grid np-deals-grid">
                        {discounted.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                formatPrice={formatPrice}
                                addingId={addingId}
                                handleAddToCart={handleAddToCart}
                                isFavorited={isFavorited}
                                toggleFavorite={toggleFavorite}
                                showDescription
                                showShippingBadges
                                discountLabel
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* Yeni Gelenler */}
            {newArrivals.length > 0 && (
                <section className="np-home-section">
                    <div className="np-section-head reveal">
                        <span className="np-section-overtitle"><Clock size={12} /> Yeni Koleksiyon</span>
                        <h2>Son Eklenen Tablolar</h2>
                        <p>Koleksiyona yeni eklenen eserler</p>
                    </div>
                    <div className="products-grid reveal reveal-stagger">
                        {newArrivals.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                formatPrice={formatPrice}
                                addingId={addingId}
                                handleAddToCart={handleAddToCart}
                                isFavorited={isFavorited}
                                toggleFavorite={toggleFavorite}
                                showDescription
                                showShippingBadges
                                discountLabel
                            />
                        ))}
                    </div>
                    <div className="np-section-cta reveal">
                        <Link to="/urunler" className="np-view-all-btn">
                            <span>Tümünü Gör</span>
                            <ArrowRight size={15} />
                        </Link>
                    </div>
                </section>
            )}

            {/* Trust Strip — footer'da var, kaldırıldı */}
        </div>
    );
}
